import React, { useEffect, useRef, useState } from "react";
import { classNames } from "../../../shared/utility";
import { InputNumber } from "primereact/inputnumber";
import RequiredFieldLabel from "../RequiredFieldLabel";
import CellSaveStatusIndicator from "./CellSaveStatusIndicator";

export default function NumberInputText(props) {
  const {
    parentClass,
    label,
    name,
    onChange,
    value,
    labelOn,
    error,
    explanation,
    ref,
    labelClass,
    suffix,
    onCommit,
    disabled,
    inputClassName,
    saveStatus = "idle",
    required = false,
    ...other
  } = props;
  const usesCommit = typeof onCommit === "function";
  const normalizedValue =
    value === null || value === undefined || value === "" ? null : Number(value);
  const [draftValue, setDraftValue] = useState(normalizedValue);
  const draftValueRef = useRef(normalizedValue);
  const cancelCommit = useRef(false);

  useEffect(() => {
    if (!usesCommit) return;

    const nextValue =
      value === null || value === undefined || value === ""
        ? null
        : Number(value);
    draftValueRef.current = nextValue;
    setDraftValue(nextValue);
  }, [usesCommit, value]);

  const commitValue = () => {
    if (!usesCommit) return;

    if (cancelCommit.current) {
      cancelCommit.current = false;
      return;
    }

    const valueToCommit = draftValueRef.current;
    if (disabled || valueToCommit === null) {
      draftValueRef.current = normalizedValue;
      setDraftValue(normalizedValue);
      return;
    }

    if (valueToCommit !== normalizedValue) {
      onCommit(valueToCommit);
    }
  };

  const showSaveStatus = usesCommit && !disabled;

  return (
    <div className={parentClass ? parentClass : null}>
      {labelOn ? (
        <div className="mb-1">
          <label
            htmlFor={name}
            className={classNames(
              labelClass ? labelClass : "text-sm",
              "block font-medium text-gray-900"
            )}
          >
            <RequiredFieldLabel required={required}>
              {label}
            </RequiredFieldLabel>
          </label>
        </div>
      ) : null}
      <div>
        <div className={showSaveStatus ? "flex items-center gap-1" : null}>
          <div className={showSaveStatus ? "min-w-0 flex-1" : null}>
            <InputNumber
              name={name}
              value={usesCommit ? draftValue : value}
              onValueChange={(event) => {
                if (usesCommit) {
                  const nextValue = event.value ?? null;
                  draftValueRef.current = nextValue;
                  setDraftValue(nextValue);
                }
                onChange?.(event);
              }}
              onBlur={commitValue}
              onKeyDown={(event) => {
                event.stopPropagation();

                if (!usesCommit) return;

                if (event.key === "Enter") {
                  event.currentTarget.blur();
                }

                if (event.key === "Escape") {
                  cancelCommit.current = true;
                  draftValueRef.current = normalizedValue;
                  setDraftValue(normalizedValue);
                  event.currentTarget.blur();
                }
              }}
              ref={ref}
              suffix={suffix}
              disabled={disabled}
              required={required}
              aria-required={required}
              inputClassName={classNames(
                inputClassName,
                saveStatus === "error" && "receiving-table-input-error",
              )}
              {...other}
            />
          </div>
          {showSaveStatus ? (
            <CellSaveStatusIndicator status={saveStatus} />
          ) : null}
        </div>

        {error ? <p className="font-medium text-red-500">{error}</p> : null}
      </div>
    </div>
  );
}
